/**
 * Trajectoire Edition Controller (EPIC-047.4)
 *
 * Page /pilotage-trajectoire-edition.html. Single page that handles both
 * creation and modification — driven by the `?trajectoryYear=N` query
 * string and whether `GET /me/trajectory?year=N` returns 200 or 404.
 *
 * Three distribution modes (uniform / seasonal / manual), live sum
 * validation (±1 € tolerance), optional per-activity breakdown, and a
 * growth slider that only appears when creating an N+1 trajectory.
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";

const MODE_LABELS = {
  uniform: "Uniforme",
  seasonal: "Saisonnée auto",
  manual: "Mois par mois",
};

const CATEGORY_LABELS = {
  bic_vente: "BIC vente de marchandises",
  bic_services: "BIC prestations de services",
  bnc: "BNC professions libérales",
};

// Zones de seuil (du plus permissif au plus engageant) — message contextuel
// non bloquant sous le champ objectif (spec §16.E « Repères seuils & plafonds »).
const ZONE_ORDER = ["under", "base", "majored", "over"];
const ZONE_NOTICE = {
  under: null,
  base: {
    text: "Objectif sous le seuil majoré : vous conservez la franchise de TVA cette année. Un dépassement répété pourrait vous y rendre redevable l'an prochain.",
    color: "var(--text-2)",
  },
  majored: {
    text: "Au-delà du seuil majoré, vous deviendriez redevable de la TVA dès le mois de franchissement.",
    color: "var(--warning-text)",
  },
  over: {
    text: "À ce rythme, vous pourriez sortir du régime micro-entreprise en cours d'année.",
    color: "var(--error-text)",
  },
};

const TOLERANCE_EUR = 1;

export default class TrajectoireEditionController extends BaseController {
  static targets = [
    "crumbCurrent", "title",
    "growthFieldset", "growthLegend", "growthRate", "growthOutput", "growthHint",
    "annualTarget", "annualTargetError",
    "modeOption", "modeSeasonal", "modeHint",
    "monthlyTbody", "monthlyInput", "monthlyTotalValue", "monthlyTotalCheck", "monthlyError",
    "splitByActivityToggle", "splitToggleWrap", "singleActivityNotice",
    "activityFields", "activityRows", "activityInput", "activityTotalRow", "activityTotalValue",
    "thresholdNotice", "thresholdsCard", "thresholdsIntro", "gaugeContainer",
    "summaryTitle", "summary", "summaryTotal", "summaryMode", "summaryAvg", "summaryGrowth", "summaryActivity",
    "formError", "formErrorMessage",
  ];

  async connect() {
    const params = new URLSearchParams(window.location.search);
    this._year = parseInt(params.get("trajectoryYear"), 10) || new Date().getFullYear();
    this._referenceYear = this._year - 1;
    this._referenceTotal = 0;
    this._isCreation = false;
    this._existing = null;
    this._activities = [];
    this._thresholdCategories = [];
    this._thresholdsByCategory = {};
    this._mixedTotal = null;

    this.updateLabels();
    await Promise.all([this.loadActivities(), this.loadThresholds()]);
    this.renderActivityFields();
    await this.loadExisting();
    await this.checkHistoryAvailability();
    this.refreshSummary();
    this.updateMonthlyEditability();
    this.recomputeMonthlyTotal();
    this.evaluateThresholds();
  }

  async loadActivities() {
    try {
      const data = await api.get("/activities");
      const list = Array.isArray(data?.activities) ? data.activities : [];
      this._activities = list.filter((a) => a.status === "active");
    } catch (_error) {
      this._activities = [];
    }
  }

  async loadThresholds() {
    try {
      const data = await api.get("/me/vat-thresholds");
      this._thresholdCategories = Array.isArray(data?.categories) ? data.categories : [];
      this._mixedTotal = data?.mixedTotal || null;
      this._thresholdsByCategory = {};
      for (const c of this._thresholdCategories) {
        this._thresholdsByCategory[c.category] = c;
      }
    } catch (_error) {
      this._thresholdCategories = [];
      this._mixedTotal = null;
      this._thresholdsByCategory = {};
    }
  }

  updateLabels() {
    if (this.hasCrumbCurrentTarget) {
      this.crumbCurrentTarget.textContent = `Définir ${this._year}`;
    }
    if (this.hasGrowthLegendTarget) {
      this.growthLegendTarget.textContent = `Croissance souhaitée vs ${this._referenceYear}`;
    }
    if (this.hasGrowthHintTarget) {
      this.growthHintTarget.textContent =
        `Appliqué à votre CA ${this._referenceYear} estimé. Vous pouvez aussi saisir directement un objectif annuel ci-dessous.`;
    }
    if (this.hasSummaryTitleTarget) {
      this.summaryTitleTarget.textContent = `Votre trajectoire ${this._year}`;
    }
  }

  async loadExisting() {
    try {
      const data = await api.get("/me/trajectory", { year: String(this._year) });
      const trajectory = data.trajectory || data;
      this._existing = trajectory;
      this._isCreation = false;
      this.applyExistingValues(trajectory);
      if (this.hasTitleTarget) {
        this.titleTarget.textContent = `Modifier ma trajectoire ${this._year}`;
      }
      if (this.hasCrumbCurrentTarget) {
        this.crumbCurrentTarget.textContent = `Modifier ${this._year}`;
      }
    } catch (error) {
      if (error.status !== 404) {
        this.showFormError(error.message || "Erreur réseau.");
        return;
      }
      this._isCreation = true;
      if (this.hasTitleTarget) this.titleTarget.textContent = `Définir ma trajectoire ${this._year}`;
      await this.loadReferenceYearForCreation();
    }

    this.toggleGrowthFieldsetVisibility();
  }

  async loadReferenceYearForCreation() {
    try {
      const data = await api.get("/me/trajectory", { year: String(this._referenceYear) });
      const trajectory = data.trajectory || data;
      this._referenceTotal = parseFloat(trajectory.totalTargetHt) || 0;
      this.applyGrowthDefault();
    } catch (error) {
      this._referenceTotal = 0;
      if (error.status !== 404) {
        this.showFormError(error.message || "Erreur réseau.");
      }
    }
  }

  toggleGrowthFieldsetVisibility() {
    if (!this.hasGrowthFieldsetTarget) return;
    const showGrowth = this._isCreation && this._referenceTotal > 0;
    this.growthFieldsetTarget.hidden = !showGrowth;
  }

  applyGrowthDefault() {
    const rate = this.hasGrowthRateTarget ? parseInt(this.growthRateTarget.value, 10) || 10 : 10;
    const computed = this._referenceTotal * (1 + rate / 100);
    if (this.hasAnnualTargetTarget) {
      this.annualTargetTarget.value = formatNumberPlain(computed);
    }
    if (this.hasGrowthOutputTarget) {
      this.growthOutputTarget.textContent = `+ ${rate} %`;
    }
    this.distributeMonthly();
  }

  applyExistingValues(trajectory) {
    const total = parseFloat(trajectory.totalTargetHt) || 0;
    if (this.hasAnnualTargetTarget) {
      this.annualTargetTarget.value = formatNumberPlain(total);
    }
    const mode = trajectory.distributionMode || "uniform";
    this.modeOptionTargets.forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      if (!input) return;
      input.checked = input.value === mode;
      label.style.borderColor = input.checked ? "var(--accent)" : "var(--border)";
      label.style.background = input.checked ? "var(--accent-soft)" : "";
    });

    const monthly = Array.isArray(trajectory.monthlyDistribution) ? trajectory.monthlyDistribution : [];
    this.monthlyInputTargets.forEach((input, i) => {
      input.value = formatNumberPlain(parseFloat(monthly[i]) || 0);
    });

    const breakdown = trajectory.breakdownByActivity;
    const hasBreakdown = breakdown && Object.keys(breakdown).length > 0;
    const canSplit = (this._activities || []).length >= 2;
    const showBreakdown = !!hasBreakdown && canSplit;
    if (this.hasSplitByActivityToggleTarget) {
      this.splitByActivityToggleTarget.checked = showBreakdown;
    }
    if (this.hasActivityFieldsTarget) {
      this.activityFieldsTarget.hidden = !showBreakdown;
    }
    if (showBreakdown) {
      this.activityInputEls().forEach((input) => {
        const id = input.dataset.activityId;
        if (id && breakdown[id] != null) {
          input.value = formatNumberPlain(parseFloat(breakdown[id]) || 0);
        }
      });
      this.recomputeActivityTotal();
    }
  }

  async checkHistoryAvailability() {
    try {
      const data = await api.get("/me/trajectory/history", { year: String(this._referenceYear) });
      const history = Array.isArray(data.history) ? data.history : (Array.isArray(data) ? data : []);
      this._historyMonths = data.monthsAvailable || history.length;
    } catch (_error) {
      this._historyMonths = 0;
    }
    this.applySeasonalAvailability();
  }

  applySeasonalAvailability() {
    if (!this.hasModeSeasonalTarget) return;
    const eligible = (this._historyMonths || 0) >= 6;
    this.modeSeasonalTarget.disabled = !eligible;
    if (!eligible) {
      if (this.modeSeasonalTarget.checked) {
        const uniformRadio = this.element.querySelector('input[name="distributionMode"][value="uniform"]');
        if (uniformRadio) {
          uniformRadio.checked = true;
          uniformRadio.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
      const label = this.modeSeasonalTarget.closest("label");
      if (label) label.style.opacity = "0.5";
      if (this.hasModeHintTarget) {
        this.modeHintTarget.textContent =
          "Le mode saisonnée automatique nécessite au moins 6 mois d'historique d'encaissements — il est désactivé pour votre compte.";
      }
    }
  }

  growthChanged() {
    if (!this.hasGrowthRateTarget) return;
    const rate = parseInt(this.growthRateTarget.value, 10) || 0;
    if (this.hasGrowthOutputTarget) this.growthOutputTarget.textContent = `+ ${rate} %`;
    if (this._referenceTotal > 0 && this.hasAnnualTargetTarget) {
      this.annualTargetTarget.value = formatNumberPlain(this._referenceTotal * (1 + rate / 100));
    }
    this.distributeMonthly();
    this.refreshSummary();
    this.evaluateThresholds();
  }

  totalChanged() {
    this.distributeMonthly();
    this.recomputeMonthlyTotal();
    this.refreshSummary();
    this.evaluateThresholds();
  }

  modeChanged() {
    this.modeOptionTargets.forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      const checked = input && input.checked;
      label.style.borderColor = checked ? "var(--accent)" : "var(--border)";
      label.style.background = checked ? "var(--accent-soft)" : "";
    });
    this.updateMonthlyEditability();
    this.distributeMonthly();
    this.recomputeMonthlyTotal();
    this.refreshSummary();
  }

  monthlyChanged() {
    this.recomputeMonthlyTotal();
    this.refreshSummary();
  }

  toggleActivity() {
    if (!this.hasActivityFieldsTarget || !this.hasSplitByActivityToggleTarget) return;
    this.activityFieldsTarget.hidden = !this.splitByActivityToggleTarget.checked;
    this.recomputeActivityTotal();
    this.refreshSummary();
    this.evaluateThresholds();
  }

  activityChanged() {
    this.recomputeActivityTotal();
    this.refreshSummary();
    this.evaluateThresholds();
  }

  /**
   * Rend dynamiquement les champs de ventilation depuis les activités
   * `active` réelles de l'organization (§16.E — aucune activité en dur).
   *  - 0 ou 1 activité → pas de ventilation possible : toggle masqué, champs
   *    vidés, message « une seule activité configurée » si exactement une.
   *  - ≥ 2 activités → une ligne clonée par activité (libellé réel +
   *    `data-activity-id`).
   */
  renderActivityFields() {
    const activities = this._activities || [];
    const canSplit = activities.length >= 2;

    if (this.hasSplitToggleWrapTarget) this.splitToggleWrapTarget.hidden = !canSplit;
    if (this.hasSingleActivityNoticeTarget) {
      this.singleActivityNoticeTarget.hidden = activities.length !== 1;
    }

    if (!canSplit) {
      if (this.hasSplitByActivityToggleTarget) this.splitByActivityToggleTarget.checked = false;
      if (this.hasActivityFieldsTarget) this.activityFieldsTarget.hidden = true;
      if (this.hasActivityRowsTarget) this.activityRowsTarget.replaceChildren();
      return;
    }

    if (!this.hasActivityRowsTarget) return;
    this.activityRowsTarget.replaceChildren();
    activities.forEach((activity) => {
      const fragment = this.cloneTemplate("tpl-trajectoire-activity-row");
      this.fillSlots(fragment, {
        label: activity.description || CATEGORY_LABELS[activity.category] || "Activité",
        sublabel: this.activitySublabel(activity),
      });
      const input = fragment.querySelector('[data-trajectoire-edition-target="activityInput"]');
      if (input) input.dataset.activityId = activity.id;
      this.activityRowsTarget.appendChild(fragment);
    });
  }

  activitySublabel(activity) {
    const category = CATEGORY_LABELS[activity.category] || activity.category || "";
    const role = activity.role === "principal" ? "principale" : "secondaire";
    return category ? `${category} · ${role}` : role;
  }

  /**
   * Inputs de ventilation, lus directement depuis le DOM (les targets
   * Stimulus sont recensés de façon asynchrone après insertion — la lecture
   * DOM est immédiate et fiable, y compris en test).
   *
   * @returns {HTMLInputElement[]}
   */
  activityInputEls() {
    if (this.hasActivityRowsTarget) {
      return Array.from(
        this.activityRowsTarget.querySelectorAll('[data-trajectoire-edition-target="activityInput"]'),
      );
    }
    return this.hasActivityInputTargets ? this.activityInputTargets : [];
  }

  updateMonthlyEditability() {
    const mode = this.getMode();
    const editable = mode === "manual";
    this.monthlyInputTargets.forEach((input) => {
      input.disabled = !editable;
    });
  }

  distributeMonthly() {
    const mode = this.getMode();
    if (mode === "manual") return;

    const total = this.getAnnualTarget();
    if (mode === "uniform") {
      const perRounded = Math.round(total / 12);
      const lastMonth = total - perRounded * 11;
      this.monthlyInputTargets.forEach((input, i) => {
        input.value = formatNumberPlain(i === 11 ? lastMonth : perRounded);
      });
      return;
    }
    if (mode === "seasonal" && this._existing && Array.isArray(this._existing.monthlyDistribution)) {
      const existingTotal = (this._existing.monthlyDistribution.reduce(
        (s, v) => s + (parseFloat(v) || 0),
        0,
      )) || 1;
      this.monthlyInputTargets.forEach((input, i) => {
        const ratio = (parseFloat(this._existing.monthlyDistribution[i]) || 0) / existingTotal;
        input.value = formatNumberPlain(total * ratio);
      });
    } else if (mode === "seasonal") {
      const per = total / 12;
      this.monthlyInputTargets.forEach((input) => {
        input.value = formatNumberPlain(per);
      });
    }
  }

  recomputeMonthlyTotal() {
    const sum = this.monthlyInputTargets.reduce(
      (acc, input) => acc + parseFrenchNumber(input.value),
      0,
    );
    const target = this.getAnnualTarget();
    const gap = sum - target;
    const ok = Math.abs(gap) <= TOLERANCE_EUR;

    if (this.hasMonthlyTotalValueTarget) {
      this.monthlyTotalValueTarget.textContent = formatEur(sum);
    }
    if (this.hasMonthlyTotalCheckTarget) {
      this.monthlyTotalCheckTarget.textContent = ok
        ? `/ ${formatEur(target)} — OK`
        : `/ ${formatEur(target)} — écart ${formatSignedEur(gap)}`;
      this.monthlyTotalCheckTarget.style.color = ok ? "" : "var(--error-text, #b3261e)";
    }
    if (this.hasMonthlyErrorTarget) {
      if (ok) {
        this.monthlyErrorTarget.hidden = true;
        this.monthlyErrorTarget.textContent = "";
      } else if (this.getMode() === "manual") {
        this.monthlyErrorTarget.hidden = false;
        this.monthlyErrorTarget.textContent =
          `La somme mensuelle (${formatEur(sum)}) doit correspondre à l'objectif annuel (${formatEur(target)}) à 1 € près.`;
      } else {
        this.monthlyErrorTarget.hidden = true;
      }
    }
  }

  recomputeActivityTotal() {
    const inputs = this.activityInputEls();
    if (inputs.length === 0) return;
    const sum = inputs.reduce(
      (acc, input) => acc + parseFrenchNumber(input.value),
      0,
    );
    const target = this.getAnnualTarget();
    const ok = Math.abs(sum - target) <= TOLERANCE_EUR;
    if (this.hasActivityTotalValueTarget) {
      this.activityTotalValueTarget.textContent = `${formatEur(sum)} / ${formatEur(target)}`;
      this.activityTotalValueTarget.style.color = ok ? "" : "var(--error-text, #b3261e)";
    }
  }

  refreshSummary() {
    const total = this.getAnnualTarget();
    if (this.hasSummaryTotalTarget) this.summaryTotalTarget.textContent = formatEur(total);
    if (this.hasSummaryModeTarget) {
      const mode = this.getMode();
      this.summaryModeTarget.textContent = MODE_LABELS[mode] || mode;
    }
    if (this.hasSummaryAvgTarget) this.summaryAvgTarget.textContent = formatEur(total / 12);

    if (this.hasSummaryGrowthTarget) {
      if (this._referenceTotal > 0) {
        const delta = ((total - this._referenceTotal) / this._referenceTotal) * 100;
        this.summaryGrowthTarget.textContent =
          `${delta >= 0 ? "+ " : "− "}${formatPercent(Math.abs(delta))}`;
        this.summaryGrowthTarget.className = delta >= 0 ? "text-success" : "text-error";
      } else {
        this.summaryGrowthTarget.textContent = "—";
        this.summaryGrowthTarget.className = "";
      }
    }

    if (this.hasSummaryActivityTarget && this.hasSplitByActivityToggleTarget) {
      this.summaryActivityTarget.textContent = this.splitByActivityToggleTarget.checked ? "Activée" : "Désactivée";
    }
  }

  /**
   * Confronte l'objectif (global ou par activité) aux seuils datés et met à
   * jour la/les jauge(s) CA-mètre + le message contextuel non bloquant
   * (§16.E). Aucune valeur réglementaire en dur : tout vient de
   * `GET /me/vat-thresholds`.
   */
  evaluateThresholds() {
    if (!this.hasThresholdsCardTarget) return;
    const cats = this._thresholdCategories || [];
    if (cats.length === 0) {
      this.thresholdsCardTarget.hidden = true;
      this.setThresholdNotice(null);
      return;
    }
    this.thresholdsCardTarget.hidden = false;

    const ventilation = this.hasSplitByActivityToggleTarget
      && this.splitByActivityToggleTarget.checked
      && (this._activities || []).length >= 2;

    const specs = [];
    let worstZone = "under";

    if (ventilation) {
      if (this.hasThresholdsIntroTarget) {
        this.thresholdsIntroTarget.textContent = "Chaque activité est confrontée aux seuils de sa catégorie.";
      }
      this.activityInputEls().forEach((input) => {
        const activity = (this._activities || []).find((a) => a.id === input.dataset.activityId);
        if (!activity) return;
        const thresholds = this._thresholdsByCategory[activity.category];
        if (!thresholds) return;
        const value = parseFrenchNumber(input.value);
        specs.push({
          title: activity.description || CATEGORY_LABELS[activity.category] || "Activité",
          value,
          thresholds,
        });
        worstZone = this.mostEngagingZone(worstZone, this.zoneFor(value, thresholds));
      });
    } else {
      const total = this.getAnnualTarget();
      if (cats.length === 1) {
        const thresholds = cats[0];
        if (this.hasThresholdsIntroTarget) {
          this.thresholdsIntroTarget.textContent =
            `Position de votre objectif sur les seuils ${CATEGORY_LABELS[thresholds.category] || "de votre activité"}.`;
        }
        specs.push({ title: CATEGORY_LABELS[thresholds.category] || "Votre activité", value: total, thresholds });
        worstZone = this.zoneFor(total, thresholds) || "under";
      } else {
        const thresholds = this.combinedThresholds();
        if (this.hasThresholdsIntroTarget) {
          this.thresholdsIntroTarget.textContent =
            "Règle combinée : plafond global et sous-limite des prestations de services. Activez la ventilation par activité pour un repère par activité.";
        }
        specs.push({ title: "Ensemble de vos activités", value: total, thresholds });
        worstZone = this.zoneFor(total, thresholds) || "under";
      }
    }

    this.renderGauges(specs);
    this.setThresholdNotice(specs.length > 0 ? worstZone : null);
  }

  /**
   * Règle combinée pour un ME multi-catégories sans ventilation : plafond
   * micro le plus haut (ou `mixedTotal` quand BIC+BNC), sous-limites TVA = les
   * plus basses (prestations de services / BNC, les plus contraignantes).
   */
  combinedThresholds() {
    const cats = this._thresholdCategories || [];
    const positives = (key) => cats.map((c) => c[key]).filter((v) => v > 0);
    const basics = positives("thresholdBasic");
    const majoreds = positives("thresholdMajored");
    const micros = positives("thresholdMicroExit");
    return {
      category: "mixed",
      thresholdBasic: basics.length ? Math.min(...basics) : 0,
      thresholdMajored: majoreds.length ? Math.min(...majoreds) : 0,
      thresholdMicroExit: this._mixedTotal?.thresholdMicroExit
        ?? (micros.length ? Math.max(...micros) : 0),
    };
  }

  /**
   * Classe une valeur dans une zone de seuil (spec §16.E) :
   *  under (sous franchise) / base (franchise→majoré) / majored (au-delà
   *  majoré) / over (au-delà plafond micro). `null` si seuils inexploitables.
   */
  zoneFor(value, thresholds) {
    if (!thresholds || !(thresholds.thresholdBasic > 0)) return null;
    if (value < thresholds.thresholdBasic) return "under";
    if (thresholds.thresholdMajored > 0 && value < thresholds.thresholdMajored) return "base";
    if (thresholds.thresholdMicroExit > 0 && value < thresholds.thresholdMicroExit) return "majored";
    return "over";
  }

  mostEngagingZone(a, b) {
    const ia = Math.max(0, ZONE_ORDER.indexOf(a));
    const ib = Math.max(0, ZONE_ORDER.indexOf(b));
    return ZONE_ORDER[Math.max(ia, ib)];
  }

  setThresholdNotice(zone) {
    if (!this.hasThresholdNoticeTarget) return;
    const notice = zone ? ZONE_NOTICE[zone] : null;
    if (!notice) {
      this.thresholdNoticeTarget.hidden = true;
      this.thresholdNoticeTarget.textContent = "";
      this.thresholdNoticeTarget.style.color = "";
      return;
    }
    this.thresholdNoticeTarget.hidden = false;
    this.thresholdNoticeTarget.textContent = notice.text;
    this.thresholdNoticeTarget.style.color = notice.color;
  }

  /**
   * (Re)construit les jauges CA-mètre dans la sidebar : une par spec (une par
   * activité en ventilation, une seule sinon). Positions des zones/ticks/
   * marqueur posées en % à partir des seuils — pas de HTML inline (clone du
   * gabarit `template` + `data-slot` + style DOM).
   *
   * @param {Array<{title: string, value: number, thresholds: object}>} specs
   */
  renderGauges(specs) {
    if (!this.hasGaugeContainerTarget) return;
    this.gaugeContainerTarget.replaceChildren();

    specs.forEach((spec) => {
      const t = spec.thresholds || {};
      const micro = t.thresholdMicroExit > 0
        ? t.thresholdMicroExit
        : Math.max(t.thresholdMajored || 0, t.thresholdBasic || 0);
      const scaleMax = Math.max(micro, spec.value) * 1.08 || 1;
      const pct = (v) => `${Math.max(0, Math.min(100, (v / scaleMax) * 100))}%`;

      const fragment = this.cloneTemplate("tpl-trajectoire-gauge");
      this.fillSlots(fragment, {
        title: spec.title,
        amount: `${formatEur(spec.value)} visé`,
        marginTva: marginLabel(t.thresholdBasic - spec.value, "franchise"),
        marginMicro: marginLabel(micro - spec.value, "plafond"),
      });

      const setZone = (name, from, to) => {
        const el = fragment.querySelector(`[data-zone="${name}"]`);
        if (!el) return;
        const lo = Math.max(0, from || 0);
        const hi = Math.min(scaleMax, to || 0);
        if (hi <= lo) {
          el.style.display = "none";
          return;
        }
        el.style.left = pct(lo);
        el.style.width = `${((hi - lo) / scaleMax) * 100}%`;
      };
      setZone("green", 0, t.thresholdBasic);
      setZone("yellow", t.thresholdBasic, t.thresholdMajored);
      setZone("orange", t.thresholdMajored, micro);
      setZone("red", micro, scaleMax);

      const setTick = (name, v) => {
        const el = fragment.querySelector(`[data-tick="${name}"]`);
        if (!el) return;
        if (!(v > 0) || v > scaleMax) {
          el.style.display = "none";
          return;
        }
        el.style.left = pct(v);
      };
      setTick("basic", t.thresholdBasic);
      setTick("majored", t.thresholdMajored);
      setTick("micro", micro);

      const marker = fragment.querySelector("[data-marker]");
      if (marker) marker.style.left = pct(spec.value);

      this.gaugeContainerTarget.appendChild(fragment);
    });
  }

  async submit(event) {
    event.preventDefault();
    this.hideFormError();

    const total = this.getAnnualTarget();
    if (total <= 0) {
      this.showFieldError(this.annualTargetErrorTarget, "L'objectif annuel doit être strictement supérieur à 0.");
      return;
    }

    const monthly = this.monthlyInputTargets.map((input) => parseFrenchNumber(input.value));
    const monthlySum = monthly.reduce((s, v) => s + v, 0);
    if (Math.abs(monthlySum - total) > TOLERANCE_EUR) {
      this.showFormError(`La somme mensuelle (${formatEur(monthlySum)}) doit correspondre à l'objectif annuel (${formatEur(total)}).`);
      return;
    }

    let breakdown = null;
    if (this.hasSplitByActivityToggleTarget && this.splitByActivityToggleTarget.checked) {
      const inputs = this.activityInputEls();
      const activityValues = inputs.map((input) => parseFrenchNumber(input.value));
      const activitySum = activityValues.reduce((s, v) => s + v, 0);
      if (Math.abs(activitySum - total) > TOLERANCE_EUR) {
        this.showFormError(`La somme par activité (${formatEur(activitySum)}) doit correspondre à l'objectif annuel (${formatEur(total)}).`);
        return;
      }
      breakdown = {};
      inputs.forEach((input, i) => {
        const id = input.dataset.activityId || `activity_${i + 1}`;
        breakdown[id] = activityValues[i];
      });
    }

    const payload = {
      year: this._year,
      totalTargetHt: total,
      distributionMode: this.getMode(),
      monthlyDistribution: monthly,
      breakdownByActivity: breakdown,
    };

    try {
      await api.put("/me/trajectory", payload);
      window.location.assign(`/pilotage-trajectoire.html?year=${this._year}`);
    } catch (error) {
      if (error.status === 422 && error.data && error.data.error && error.data.error.details) {
        this.showFormError(error.message || "Données invalides.");
      } else {
        this.showFormError(error.message || "Erreur réseau.");
      }
    }
  }

  getMode() {
    const checked = this.element.querySelector('input[name="distributionMode"]:checked');
    return checked ? checked.value : "uniform";
  }

  getAnnualTarget() {
    if (!this.hasAnnualTargetTarget) return 0;
    return parseFrenchNumber(this.annualTargetTarget.value);
  }

  showFormError(message) {
    if (this.hasFormErrorTarget) {
      this.formErrorTarget.hidden = false;
      if (this.hasFormErrorMessageTarget) this.formErrorMessageTarget.textContent = ` ${message}`;
    }
  }

  hideFormError() {
    if (this.hasFormErrorTarget) {
      this.formErrorTarget.hidden = true;
      if (this.hasFormErrorMessageTarget) this.formErrorMessageTarget.textContent = "";
    }
  }

  showFieldError(errorTarget, message) {
    if (errorTarget) {
      errorTarget.hidden = false;
      errorTarget.textContent = message;
    }
  }
}

function parseFrenchNumber(value) {
  if (typeof value !== "string") value = String(value || "");
  const cleaned = value.replace(/[\s ]/g, "").replace(",", ".");
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatNumberPlain(value) {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(value));
}

function formatEur(value) {
  const n = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  return `${n} €`;
}

function formatSignedEur(value) {
  if (!Number.isFinite(value) || value === 0) return formatEur(0);
  const abs = formatEur(Math.abs(value));
  return value < 0 ? `− ${abs}` : `+ ${abs}`;
}

function formatPercent(value) {
  return `${new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)} %`;
}

// Libellé de marge restante avant un seuil. Marge ≤ 0 → seuil déjà dépassé.
function marginLabel(margin, kind) {
  if (margin > 0) return formatEur(margin);
  return kind === "franchise" ? "Seuil dépassé" : "Plafond dépassé";
}
